import React, { useEffect, useRef, useState } from "react";
import API_BASE_URL from 'config/api';
import {
  Grid,
  Divider,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Snackbar,
  Typography,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { ConfigCard } from "styles";
import { BlueButton } from "styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MDButton from "components/MDButton";
import BannerPreview from "components/BannerPreview";
import LivePreview from "components/LivePreview";
import { useLocation } from "react-router-dom";
import {
  resolveLanguageSelection,
  SUPPORTED_BANNER_LANGUAGES as LANGUAGES,
} from "services/bannerLanguage";

const resolveRequestedDomain = (settings, search) => {
  const entries = Object.entries(settings || {});
  const requestedSiteId = new URLSearchParams(search).get("siteId");
  if (!requestedSiteId) return entries[0]?.[0] || "";
  const match = entries.find(([, value]) => String(value?.id) === String(requestedSiteId));
  return match?.[0] || "";
};

const resolveRequestedTenantId = (me, search) => {
  const clients = me?.user?.clients || [];
  const requestedSiteId = new URLSearchParams(search).get("siteId");
  if (!requestedSiteId) return clients[0]?.clientId || "";
  const client = clients.find((item) =>
    (item.sites || []).some((site) => String(site.siteId) === String(requestedSiteId))
  );
  return client?.clientId || "";
};

const BannerLanguage = () => {
  const location = useLocation();
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [bannerSettings, setBannerSettings] = useState({});
  const [userMeData, setUserMeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewTabValue, setPreviewTabValue] = useState(0);
  const [activeLang, setActiveLang] = useState("");
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functionality: false,
    analytics: false,
    marketing: false,
  });
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentDomainSettings, setCurrentDomainSettings] = useState(null);
  const [currentLanguageSettings, setCurrentLanguageSettings] = useState(null);
  const [cookies, setCookies] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [addedLanguages, setAddedLanguages] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newLang, setNewLang] = useState("");
  const [languagesToDelete, setLanguagesToDelete] = useState([]);
  const [originalActiveLang, setOriginalActiveLang] = useState("");
  const [multiLanguage, setMultiLanguage] = useState(true);
  const bannerRequestRef = useRef(0);
  const cookieRequestRef = useRef(0);

  const computeMultiLanguage = (domainSettings) => {
    const langs = Object.keys(domainSettings?.bannerSettings || {});
    if (langs.length === 0) return true;
    return !!domainSettings?.hasMultilanguage;
  };

  useEffect(() => {
    const loadUserData = async () => {
      const cachedUserData = sessionStorage.getItem("userMeData");
      if (cachedUserData) {
        setUserMeData(JSON.parse(cachedUserData));
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/me`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        });
        const data = await response.json();
        sessionStorage.setItem("userMeData", JSON.stringify(data));
        setUserMeData(data);
      } catch (error) {
        console.error("Error loading user data:", error);
        showSnackbar("Kullanıcı verileri yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  useEffect(() => {
    const requestId = bannerRequestRef.current + 1;
    bannerRequestRef.current = requestId;
    const controller = new AbortController();
    const loadBannerSettings = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        setBannerSettings({});
        setDomains([]);
        setSelectedDomain("");
        setCurrentDomainSettings(null);
        setCurrentLanguageSettings(null);
        setAddedLanguages([]);
        setLanguages([]);
        setActiveLang("");
        setOriginalActiveLang("");
        const tenantId = resolveRequestedTenantId(userMeData, location.search);
        if (!tenantId) {
          setBannerSettings({});
          setDomains([]);
          setSelectedDomain("");
          showSnackbar("Seçili site bu hesaba bağlı değil", "error");
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/banner-settings/by-tenant?tenantId=${tenantId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error("Banner ayarları alınamadı");
        const data = await response.json();
        if (requestId !== bannerRequestRef.current) return;
        setBannerSettings(data);
        setDomains(Object.keys(data));

        if (Object.keys(data).length > 0) {
          const firstDomain = resolveRequestedDomain(data, location.search);
          if (!firstDomain) {
            setSelectedDomain("");
            setCurrentDomainSettings(null);
            setCurrentLanguageSettings(null);
            showSnackbar("Seçili site için banner ayarı bulunamadı", "error");
            return;
          }
          setSelectedDomain(firstDomain);
          setCurrentDomainSettings(data[firstDomain]);

          const domainSettingsForCompute = data[firstDomain];
          setMultiLanguage(computeMultiLanguage(domainSettingsForCompute));

          if (data[firstDomain]?.bannerSettings?.[activeLang]) {
            setCurrentLanguageSettings(
              data[firstDomain]?.bannerSettings?.[activeLang]
            );
          } else {
            const availableLangs = Object.keys(
              data[firstDomain]?.bannerSettings || {}
            );
            if (availableLangs.length > 0) {
              setActiveLang(availableLangs[0]);
              setOriginalActiveLang(availableLangs[0]);
              setCurrentLanguageSettings(
                data[firstDomain]?.bannerSettings?.[availableLangs[0]]
              );
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError" || requestId !== bannerRequestRef.current) return;
        console.error("Error loading banner settings:", error);
        showSnackbar("Banner ayarları yüklenirken hata oluştu", "error");
      } finally {
        if (requestId === bannerRequestRef.current) setLoading(false);
      }
    };

    loadBannerSettings();
    return () => controller.abort();
  }, [userMeData, location.search]);

  useEffect(() => {
    const requestId = cookieRequestRef.current + 1;
    cookieRequestRef.current = requestId;
    const controller = new AbortController();
    const loadCookieData = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const tenantId = resolveRequestedTenantId(userMeData, location.search);
        if (!tenantId) {
          setCookies({});
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/cookie-definition/all?tenantId=${tenantId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error("Çerezler alınamadı");
        const data = await response.json();
        if (requestId !== cookieRequestRef.current) return;

        setCookies(Object.keys(data).length > 0 ? data : {});
      } catch (error) {
        if (error.name === "AbortError" || requestId !== cookieRequestRef.current) return;
        console.error("Error loading cookies:", error);
        showSnackbar("Çerezler yüklenirken hata oluştu", "error");
      } finally {
        if (requestId === cookieRequestRef.current) setLoading(false);
      }
    };

    loadCookieData();
    return () => controller.abort();
  }, [userMeData, location.search]);

  useEffect(() => {
    if (selectedDomain && bannerSettings?.[selectedDomain]) {
      const domainSettings = bannerSettings[selectedDomain];
      const languageSelection = resolveLanguageSelection(domainSettings);

      setMultiLanguage(computeMultiLanguage(domainSettings));

      setCurrentDomainSettings(domainSettings);
      setActiveLang(languageSelection.activeCode);
      setOriginalActiveLang(languageSelection.activeCode);
      setCurrentLanguageSettings(
        domainSettings?.bannerSettings?.[languageSelection.activeCode] || null
      );
      setAddedLanguages(languageSelection.persistedCodes);
      setLanguages(languageSelection.options);
      setLanguagesToDelete([]);
    } else {
      setCurrentDomainSettings(null);
      setCurrentLanguageSettings(null);
      setAddedLanguages([]);
      setLanguages([]);
    }
  }, [selectedDomain]);

  useEffect(() => {
    if (
      currentDomainSettings &&
      currentDomainSettings?.bannerSettings?.[activeLang]
    ) {
      setCurrentLanguageSettings(
        currentDomainSettings?.bannerSettings?.[activeLang]
      );
    }
  }, [activeLang, currentDomainSettings]);

  const handleCategoryToggle = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDomainChange = (event) => {
    setSelectedDomain(event.target.value);
  };

  const handlePreviewTabChange = (event, newValue) => {
    setPreviewTabValue(newValue);
  };

  const handleLangChange = (e) => {
    setActiveLang(e.target.value);
  };

  const handlePreferenceChange = (category) => (event) => {
    setCookiePreferences({
      ...cookiePreferences,
      [category]: event.target.checked,
    });
  };

  const handleAcceptAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleCustomize = () => {
    setOpenModal(!openModal);
  };

  const handleAcceptSelection = () => {
    setOpenModal(false);
  };

  const reloadBannerSettings = async () => {
    try {
      const tenantId = resolveRequestedTenantId(userMeData, location.search);
      if (!tenantId) throw new Error("Seçili site bu hesaba bağlı değil");
      const res = await fetch(
        `${API_BASE_URL}/banner-settings/by-tenant?tenantId=${tenantId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        }
      );
      if (!res.ok) throw new Error("Banner ayarları yenilenemedi");
      const settings = await res.json();
      sessionStorage.removeItem("bannerSettings");
      setBannerSettings(settings);
      setDomains(Object.keys(settings));

      if (Object.keys(settings).length > 0) {
        const domainSettings = settings?.[selectedDomain];
        const languageSelection = resolveLanguageSelection(domainSettings);

        setMultiLanguage(computeMultiLanguage(domainSettings));

        setCurrentDomainSettings(domainSettings);
        setAddedLanguages(languageSelection.persistedCodes);
        setLanguages(languageSelection.options);
        setCurrentLanguageSettings(
          domainSettings?.bannerSettings?.[languageSelection.activeCode] || null
        );
        setActiveLang(languageSelection.activeCode);
        setOriginalActiveLang(languageSelection.activeCode);
      }
    } catch (error) {
      console.error("Veri yenileme hatası:", error);
      showSnackbar("Veri yenilenirken hata oluştu", "error");
    }
  };

  const handleSave = async () => {
    if (!currentDomainSettings || !selectedDomain || !userMeData) return;
    if (!activeLang) {
      showSnackbar("Önce bir dil ekleyin ve varsayılan dili seçin", "warning");
      return;
    }

    try {
      setSaving(true);

      if (multiLanguage) {
        const wasZeroLanguage = (addedLanguages?.length || 0) === 0;

        const addBannerSettingLang = languages
          .filter((lang) => !addedLanguages?.includes(lang.code))
          .map((lang) => lang.code);

        const shouldUpdateDefaultLang =
          activeLang !== currentDomainSettings?.defaultLanguage;

        const hasLanguagesToDelete = languagesToDelete.length > 0;

        if (
          addBannerSettingLang.length === 0 &&
          !shouldUpdateDefaultLang &&
          !hasLanguagesToDelete
        ) {
          showSnackbar(
            "Yeni bir dil eklenmedi veya varsayılan dil değişmedi",
            "info"
          );
          return;
        }

        if (shouldUpdateDefaultLang) {
          const siteId = currentDomainSettings?.id;
          const res = await fetch(
            `${API_BASE_URL}/sites/${siteId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
              body: JSON.stringify({ defaultLanguage: activeLang }),
            }
          );

          if (!res.ok) throw new Error("Varsayılan dil güncellenemedi");

          const updatedDomainSettings = {
            ...currentDomainSettings,
            defaultLanguage: activeLang,
          };
          setCurrentDomainSettings(updatedDomainSettings);

          const updatedSettings = { ...bannerSettings };
          updatedSettings[selectedDomain] = {
            ...updatedSettings?.[selectedDomain],
            defaultLanguage: activeLang,
          };
          sessionStorage.removeItem("bannerSettings");
          setBannerSettings(updatedSettings);
        }

        if (hasLanguagesToDelete) {
          for (const langToDelete of languagesToDelete) {
            const deleteResponse = await fetch(
              `${API_BASE_URL}/banner-settings`,
              {
                method: "DELETE",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${sessionStorage.getItem(
                    "authToken"
                  )}`,
                },
                body: JSON.stringify({
                  siteId: currentDomainSettings?.id,
                  language: langToDelete,
                }),
              }
            );

            if (!deleteResponse.ok) {
              const errorData = await deleteResponse.json();
              throw new Error(
                errorData.error || `${langToDelete} dili silinemedi`
              );
            }
          }
          setLanguagesToDelete([]);
        }

        if (addBannerSettingLang.length > 0) {
          const payloadBannerSettings = {
            siteId: currentDomainSettings?.id,
            languages: addBannerSettingLang,
          };

          const response = await fetch(
            `${API_BASE_URL}/banner-settings`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
              body: JSON.stringify(payloadBannerSettings),
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "API error occurred");
          }
        }

        if (wasZeroLanguage && addBannerSettingLang.length > 0) {
          const siteId = currentDomainSettings?.id;
          const newDefaultLang = addBannerSettingLang.includes(activeLang)
            ? activeLang
            : addBannerSettingLang[0];

          const putRes = await fetch(
            `${API_BASE_URL}/sites/${siteId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
              body: JSON.stringify({ defaultLanguage: newDefaultLang }),
            }
          );

          if (!putRes.ok) {
            const err = await putRes.json().catch(() => ({}));
            throw new Error(
              err.error ||
                "İlk dil eklendikten sonra varsayılan dil güncellenemedi"
            );
          }

          const updatedDomainSettings = {
            ...currentDomainSettings,
            defaultLanguage: newDefaultLang,
          };
          setCurrentDomainSettings(updatedDomainSettings);

          const updatedBannerSettings = { ...bannerSettings };
          updatedBannerSettings[selectedDomain] = {
            ...updatedBannerSettings?.[selectedDomain],
            defaultLanguage: newDefaultLang,
          };
          setBannerSettings(updatedBannerSettings);
          sessionStorage.removeItem("bannerSettings");

          setActiveLang(newDefaultLang);
          setOriginalActiveLang(newDefaultLang);
        }

        await reloadBannerSettings();
      } else {
        if ((addedLanguages?.length || 0) === 0) {
          const createResponse = await fetch(`${API_BASE_URL}/banner-settings`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
            body: JSON.stringify({
              siteId: currentDomainSettings?.id,
              languages: [activeLang],
            }),
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.json().catch(() => ({}));
            throw new Error(errorData.error || "Varsayılan dil oluşturulamadı");
          }

          const siteResponse = await fetch(
            `${API_BASE_URL}/sites/${currentDomainSettings?.id}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
              body: JSON.stringify({ defaultLanguage: activeLang }),
            }
          );
          if (!siteResponse.ok) throw new Error("Varsayılan dil güncellenemedi");
        } else if (activeLang === originalActiveLang) {
          showSnackbar("Dil değişmedi", "info");
          return;
        } else {
          const changeLanguagePayload = {
            siteId: currentDomainSettings?.id,
            fromLang: originalActiveLang,
            toLang: activeLang,
          };

          const response = await fetch(
            `${API_BASE_URL}/banner-settings/change-language`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
              body: JSON.stringify(changeLanguagePayload),
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Dil değiştirme işlemi başarısız");
          }
        }

        setOriginalActiveLang(activeLang);
        await reloadBannerSettings();
      }

      showSnackbar("Dil ayarları başarıyla kaydedildi", "success");
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      showSnackbar(error.toString(), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultLangChange = (e) => {
    const newLang = e.target.value;
    setActiveLang(newLang);
  };

  const handleAddLanguage = () => {
    if (newLang && !languages?.some((l) => l.code === newLang)) {
      const langObj = LANGUAGES.find((l) => l.code === newLang) || {
        code: newLang,
        label: newLang,
      };
      setLanguages((prev) => [...prev, langObj]);
      if ((languages?.length || 0) === 0) {
        setActiveLang(newLang);
      }
    }
    setAddOpen(false);
    setNewLang("");
  };

  const handleDeleteLanguage = (code) => {
    if (addedLanguages?.includes(code)) {
      setLanguagesToDelete((prev) => [...prev, code]);
    }

    const filtered = languages?.filter((l) => l.code !== code);
    setLanguages(filtered);

    if (activeLang === code) {
      setActiveLang(filtered?.[0]?.code || "");
    }
  };

  const handleChangeLanguage = () => {
    if (newLang && newLang !== activeLang) {
      setActiveLang(newLang);
      const newLangObj = LANGUAGES.find((l) => l.code === newLang);
      if (newLangObj) {
        setLanguages([newLangObj]);
      }
    }
    setAddOpen(false);
    setNewLang("");
  };

  const availableLanguages = multiLanguage
    ? LANGUAGES.filter(
        (l) => !languages?.some((added) => added.code === l.code)
      )
    : LANGUAGES.filter((l) => l.code !== activeLang);

  const themeSettings = currentDomainSettings?.themeSettings;
  const availableLangs =
    currentDomainSettings?.bannerSettings &&
    Object.keys(currentDomainSettings.bannerSettings);

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ConfigCard>
              <MDBox p={3}>
                <MDTypography
                  variant="h5"
                  fontWeight="medium"
                  mb={3}
                  color="info"
                >
                  Dil Ayarları
                </MDTypography>

                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Domain
                </MDTypography>
                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                      background: "#fafbfc",
                      fontSize: 16,
                      padding: "4px 12px",
                      "& .MuiSelect-icon": {
                        color: "#000",
                        right: "12px",
                      },
                    },
                  }}
                >
                  <Select
                    value={selectedDomain}
                    onChange={handleDomainChange}
                    IconComponent={(props) => (
                      <KeyboardArrowDownIcon {...props} fontSize="medium" />
                    )}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                      borderRadius: "12px",
                      ".MuiSelect-select": {
                        padding: "14px 32px 14px 12px",
                      },
                      "& .MuiSelect-icon": {
                        display: "block !important",
                      },
                    }}
                  >
                    {domains?.map((domain) => (
                      <MenuItem key={domain} value={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Varsayılan Dil
                </MDTypography>

                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                      background: "#fafbfc",
                      fontSize: 16,
                      padding: "4px 12px",
                      "& .MuiSelect-icon": {
                        color: "#000",
                        right: "12px",
                      },
                    },
                  }}
                >
                  <Select
                    value={activeLang}
                    onChange={handleDefaultLangChange}
                    disabled={(languages?.length || 0) === 0}
                    IconComponent={(props) => (
                      <KeyboardArrowDownIcon {...props} fontSize="medium" />
                    )}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                      borderRadius: "12px",
                      ".MuiSelect-select": {
                        padding: "14px 32px 14px 12px",
                      },
                      "& .MuiSelect-icon": {
                        display: "block !important",
                      },
                    }}
                  >
                    {languages?.map((lang) => (
                      <MenuItem
                        key={lang?.code}
                        value={lang?.code}
                        sx={{
                          fontSize: 16,
                          "&.Mui-selected": {
                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                          },
                        }}
                      >
                        {lang?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />

                {multiLanguage && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => setAddOpen(true)}
                    sx={{
                      borderRadius: 2,
                      py: 1.2,
                      fontWeight: 500,
                      fontSize: 15,
                      mb: 2,
                      textTransform: "none",
                    }}
                  >
                    Dil Ekle
                  </MDButton>
                )}

                {!multiLanguage && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    startIcon={<ChangeCircleIcon />}
                    onClick={() => setAddOpen(true)}
                    sx={{
                      borderRadius: 2,
                      py: 1.2,
                      fontWeight: 500,
                      fontSize: 15,
                      mb: 2,
                      textTransform: "none",
                    }}
                  >
                    Dil Değiştir
                  </MDButton>
                )}

                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                >
                  {multiLanguage ? "Eklenen Diller" : "Seçili Dil"}
                </MDTypography>
                <List dense sx={{ mt: 1 }}>
                  {languages?.map((lang) => (
                    <ListItem
                      key={lang?.code}
                      sx={{
                        bgcolor:
                          activeLang === lang?.code
                            ? "rgba(24, 144, 255, 0.09)"
                            : "inherit",
                        borderRadius: 1,
                        mb: 1,
                        px: 1,
                        py: 1,
                      }}
                    >
                      <ListItemText primary={lang?.label} />
                      {multiLanguage && languages?.length > 1 && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteLanguage(lang?.code)}
                            sx={{ mr: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 3 }} />

                <BlueButton
                  fullWidth
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </BlueButton>
              </MDBox>
            </ConfigCard>
          </Grid>

          <Grid item xs={12} md={8}>
            <LivePreview
              cookieContentState={{
                banner: currentLanguageSettings ? (
                  <BannerPreview
                    themeSettings={themeSettings}
                    currentLanguageSettings={currentLanguageSettings}
                    currentDomainSettings={currentDomainSettings}
                    cookies={cookies}
                    activeLang={activeLang}
                    previewTabValue={previewTabValue}
                    openModal={openModal}
                    cookiePreferences={cookiePreferences}
                    expandedCategories={expandedCategories}
                    availableLangs={availableLangs}
                    onLangChange={handleLangChange}
                    onPreviewTabChange={handlePreviewTabChange}
                    onCustomize={handleCustomize}
                    onAcceptAll={handleAcceptAll}
                    onRejectAll={handleRejectAll}
                    onAcceptSelection={handleAcceptSelection}
                    onPreferenceChange={handlePreferenceChange}
                    onCategoryToggle={handleCategoryToggle}
                    onSnackbar={showSnackbar}
                  />
                ) : null,
              }}
              selectedDomain={selectedDomain}
              defaultLanguage={currentDomainSettings?.defaultLanguage}
            />
          </Grid>
        </Grid>
      </MDBox>

      <Snackbar
        open={snackbar?.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar?.severity}
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {multiLanguage? "Dil Seç" : "Dil Değiştir"}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 1,
            pb: 2,
            background: "#fcfcfd",
          }}
        >
          <FormControl
            fullWidth
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                background: "#f6f8fa",
                fontSize: 16,
                padding: "2px 12px",
                "& .MuiSelect-icon": {
                  color: "#607d8b",
                  right: "10px",
                  display: "block !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                  borderWidth: 1,
                },
              },
              "& .MuiInputLabel-root": {
                fontWeight: 500,
                color: "#607d8b",
                fontSize: 15,
                "&.Mui-focused": {
                  color: "#1976d2",
                },
              },
            }}
          >
            <InputLabel>Dil</InputLabel>
            <Select
              value={newLang}
              onChange={(e) => setNewLang(e.target.value)}
              label="Dil"
              IconComponent={(props) => (
                <KeyboardArrowDownIcon {...props} fontSize="small" />
              )}
              sx={{
                minHeight: 44,
                fontSize: 16,
                borderRadius: "10px",
                ".MuiSelect-select": {
                  padding: "14px 32px 14px 12px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "10px",
                    marginTop: "6px",
                    boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
                    "& .MuiMenuItem-root": {
                      fontSize: 16,
                      minHeight: "auto",
                      padding: "8px 16px",
                    },
                  },
                },
              }}
            >
              {availableLanguages?.length === 0 && (
                <MenuItem value="" disabled sx={{ fontSize: 16 }}>
                  {multiLanguage
                    ? "Tüm diller eklendi."
                    : "Başka dil mevcut değil."}
                </MenuItem>
              )}
              {availableLanguages?.map((lang) => (
                <MenuItem
                  key={lang?.code}
                  value={lang?.code}
                  sx={{
                    fontSize: 16,
                    "&.Mui-selected": {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.12)",
                    },
                  }}
                >
                  {lang?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAddOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              fontSize: 15,
              px: 2.5,
            }}
          >
            Vazgeç
          </Button>
          <BlueButton
            onClick={multiLanguage ? handleAddLanguage : handleChangeLanguage}
            disabled={!newLang}
          >
            {multiLanguage ? "Ekle" : "Değiştir"}
          </BlueButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default BannerLanguage;
